import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { pickI18n } from '../i18n'
import { ErrorState, Loading } from './states.jsx'

const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' }

/** Top feeders: meals fed + meals paid for, this month or all time, per city. */
export default function LeaderboardPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { user } = useAuth()
  const [period, setPeriod] = useState('month')
  const [city, setCity] = useState('')
  const feeders = useApi(() => api.feeders())
  const board = useApi(() => api.leaderboard(period, city || undefined), [period, city, user?.uid])
  const cities = [...new Set((feeders.data ?? []).map((f) => f.city).filter(Boolean))]

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('leaderboard.title')}</h1>
        <p className="text-lg text-muted">{t('leaderboard.lead')}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {['month', 'all'].map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={period === p}
            onClick={() => setPeriod(p)}
            className={`cursor-pointer rounded-full px-5 py-2.5 font-extrabold ${period === p ? 'bg-ink text-on-ink' : 'border-[1.5px] border-edge text-ink'}`}
          >
            {t(`leaderboard.${p}`)}
          </button>
        ))}
        <select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          aria-label={t('leaderboard.city')}
          className="ml-auto h-11 rounded-full border-[1.5px] border-edge bg-surface px-4 font-bold text-ink"
        >
          <option value="">{t('leaderboard.allCyprus')}</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {board.loading && !board.data ? (
        <Loading />
      ) : board.error ? (
        <ErrorState />
      ) : board.data.rows.length ? (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {board.data.rows.map((r) => (
            <li
              key={r.place}
              className={`flex items-center gap-4 rounded-2xl border-[1.5px] px-4 py-3 ${r.me ? 'border-teal bg-mint' : 'border-line bg-surface'}`}
            >
              <span className="w-9 text-center text-xl font-black">{MEDAL[r.place] ?? r.place}</span>
              <span className="flex flex-1 flex-col">
                <span className="font-extrabold">
                  {r.name ?? t('leaderboard.anonymous')}
                  {r.me ? ` · ${t('leaderboard.you')}` : ''}
                </span>
                {r.rank ? (
                  <span className="text-sm text-subtle">
                    {r.rank.emoji} {pickI18n(r.rank.name, lang)}
                  </span>
                ) : null}
              </span>
              <span className="text-right">
                <span className="block text-xl font-black text-teal">{r.meals}</span>
                <span className="text-xs font-bold text-subtle">{t('cabinet.meals', { count: r.meals })}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-muted">{t('leaderboard.empty')}</p>
      )}

      {board.data?.me && !board.data.rows.some((r) => r.me) ? (
        <p className="m-0 rounded-2xl bg-paper p-4 font-bold">
          {board.data.me.hidden
            ? t('leaderboard.youHidden', { count: board.data.me.meals })
            : t('leaderboard.yourPlace', { place: board.data.me.place, count: board.data.me.meals })}
        </p>
      ) : null}
      <p className="m-0 text-sm text-subtle">
        {t('leaderboard.privacy')} <Link to="/profile" className="text-teal">{t('nav.cabinet')}</Link>
      </p>
    </main>
  )
}
